// src/components/Leaderboard.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthContext';
import tournamentService from '../services/tournamentService';
import leaderboardService from '../services/leaderboardService';
import '../styles/Leaderboard.css';
import { formatInTimeZone } from 'date-fns-tz';
import SEO from './SEO';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    Typography,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputAdornment,
    Chip
} from '@mui/material';
import { Search, Schedule, CalendarToday } from '@mui/icons-material';

const Leaderboard = () => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);

    const [tournaments, setTournaments] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);

    // Měsíční filtrování
    const [availableMonths, setAvailableMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('all'); // 'all' nebo 'year-month' (např. '2025-10')

    // DTO data + server pagination
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStep, setLoadingStep] = useState('');
    const [error, setError] = useState(null);

    // Helper pro formátování měsíců
    const getMonthName = (monthNum) => {
        const monthNames = {
            1: t('months.january', { defaultValue: 'Leden' }),
            2: t('months.february', { defaultValue: 'Únor' }),
            3: t('months.march', { defaultValue: 'Březen' }),
            4: t('months.april', { defaultValue: 'Duben' }),
            5: t('months.may', { defaultValue: 'Květen' }),
            6: t('months.june', { defaultValue: 'Červen' }),
            7: t('months.july', { defaultValue: 'Červenec' }),
            8: t('months.august', { defaultValue: 'Srpen' }),
            9: t('months.september', { defaultValue: 'Září' }),
            10: t('months.october', { defaultValue: 'Říjen' }),
            11: t('months.november', { defaultValue: 'Listopad' }),
            12: t('months.december', { defaultValue: 'Prosinec' })
        };
        return monthNames[monthNum] || monthNum;
    };

    // Format date helper function for relative time
    const formatCalculatedAt = (calculatedAt) => {
        if (!calculatedAt) return '';
        
        const date = new Date(calculatedAt);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) {
            return t('leaderboard.justNow', { defaultValue: 'Just now' });
        } else if (diffInMinutes < 60) {
            return t('leaderboard.minutesAgo', { 
                defaultValue: '{{count}} minutes ago', 
                count: diffInMinutes 
            });
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return t('leaderboard.hoursAgo', { 
                defaultValue: '{{count}} hours ago', 
                count: hours 
            });
        } else {
            return date.toLocaleDateString('cs-CZ', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    // Format exact date and time in user's timezone
    const formatExactDateTime = (calculatedAt) => {
        if (!calculatedAt) return '';
        
        const utcDate = calculatedAt.endsWith('Z') ? calculatedAt : `${calculatedAt}Z`;
        
        return formatInTimeZone(
            new Date(utcDate), 
            Intl.DateTimeFormat().resolvedOptions().timeZone,
            'dd.MM.yyyy HH:mm'
        );
    };

    // Get the most recent calculatedAt from current items for display
    const getLastUpdated = () => {
        if (!items || items.length === 0) return null;
        
        const mostRecent = items.reduce((latest, item) => {
            if (!item.calculatedAt) return latest;
            if (!latest || !latest.calculatedAt) return item;
            return new Date(item.calculatedAt) > new Date(latest.calculatedAt) ? item : latest;
        }, null);
        
        return mostRecent?.calculatedAt;
    };

    // Fetch all tournaments on component mount
    useEffect(() => {
        const fetchTournaments = async () => {
            try {
                setLoading(true);
                setLoadingStep(t('loadings.fetchingTournaments', { defaultValue: 'Načítání turnajů...' }));
                setLoadingProgress(20);

                const tournamentsWithLeaderboards = await tournamentService.getTournamentsWithLeaderboards();
                setTournaments(tournamentsWithLeaderboards);

                if (tournamentsWithLeaderboards.length > 0) {
                    setSelectedTournament(tournamentsWithLeaderboards[0].id);
                }
                setLoadingProgress(60);
            } catch (_err) {
                setError('Failed to load tournaments.');
            }
        };
        fetchTournaments();
    }, [t]);

    // Fetch available months when tournament changes
    useEffect(() => {
        if (!selectedTournament) return;

        const fetchAvailableMonths = async () => {
            try {
                const months = await leaderboardService.getAvailableMonths(selectedTournament);
                setAvailableMonths(months);
                // Reset na 'all' při změně turnaje
                setSelectedMonth('all');
            } catch (err) {
                console.error('Failed to load available months:', err);
                setAvailableMonths([]);
            }
        };

        fetchAvailableMonths();
    }, [selectedTournament]);

    // Fetch leaderboard data when tournament/month/page/size changes
    useEffect(() => {
        if (!selectedTournament) return;

        const fetchLeaderboard = async () => {
            setLoading(true);
            setError(null);
            setLoadingStep(t('loadings.fetchingLeaderboard', { defaultValue: 'Načítání žebříčku...' }));
            setLoadingProgress(60);

            try {
                let result;

                if (selectedMonth === 'all') {
                    // Celkový leaderboard
                    result = await leaderboardService.getLeaderboardByTournament(
                        selectedTournament, 
                        page, 
                        rowsPerPage
                    );
                } else {
                    // Měsíční leaderboard
                    const [year, month] = selectedMonth.split('-').map(Number);
                    result = await leaderboardService.getMonthlyLeaderboard(
                        selectedTournament, 
                        year, 
                        month, 
                        page, 
                        rowsPerPage
                    );
                }

                if (result && Array.isArray(result.items)) {
                    setItems(result.items || []);
                    setTotal(typeof result.total === 'number' ? result.total : (result.items || []).length);

                    if (typeof result.page === 'number' && result.page !== page) {
                        setPage(result.page);
                    }
                } else if (Array.isArray(result)) {
                    setItems(result);
                    setTotal(result.length);
                } else {
                    setItems([]);
                    setTotal(0);
                }

                setLoadingProgress(90);
                setTimeout(() => {
                    setLoading(false);
                    setLoadingProgress(100);
                }, 200);
            } catch (_err) {
                setError('Failed to load leaderboard data.');
                setItems([]);
                setTotal(0);
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, [selectedTournament, selectedMonth, page, rowsPerPage, t]);

    const handleTournamentChange = (event) => {
        setSelectedTournament(event.target.value);
        setPage(0);
    };

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
        setPage(0);
    };

    const handleChangePage = (_event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Data přicházejí již seřazená z backendu
    const rankedPageItems = (items || []).map((p, index) => ({
        ...p,
        rank: page * rowsPerPage + index + 1,
    }));

    const lastUpdated = getLastUpdated();

    return (
        <div className="leagues-container">
            <SEO 
                title={t('leaderboard.title', { defaultValue: "Žebříček hráčů" })}
                description="Porovnej své výsledky s nejlepšími tipéry v celkovém hodnocení Scoreo."
            />
            <div className="leagues-section">
                <div className="leagues-section-header">
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', position: 'relative' }}>
                        <h3 className="leagues-section-title" style={{ margin: 0, marginRight: '8px' }}>
                            {t('rankings')}
                        </h3>
                        <div 
                            style={{ position: 'relative', display: 'inline-block' }}
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                        >
                            <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#6B7280',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#4DA8DA'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#6B7280'}
                            >
                                i
                            </div>
                            {showTooltip && (
                                <div style={{
                                    position: 'absolute',
                                    top: '30px',
                                    left: '0px',
                                    backgroundColor: '#1e293b',
                                    color: '#f1f5f9',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    lineHeight: '1.4',
                                    maxWidth: '500px',
                                    minWidth: '450px',
                                    zIndex: 9999,
                                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                                    fontFamily: 'Montserrat, sans-serif'
                                }}>
                                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                                        {t('leaderboard.description', { 
                                            defaultValue: 'This page shows the overall ranking among all users participating in a given tournament. It covers both private and public leagues and displays the maximum number of points each user has achieved across all their league participations.' 
                                        })}
                                    </div>
                                    {lastUpdated && (
                                        <div style={{ 
                                            fontSize: '12px', 
                                            color: '#e2e8f0',
                                            borderTop: '1px solid #475569',
                                            paddingTop: '6px',
                                            marginTop: '6px'
                                        }}>
                                            <span style={{ fontWeight: '500' }}>
                                                {t('leaderboard.lastUpdated', { defaultValue: 'Last updated' })}:
                                            </span> {formatExactDateTime(lastUpdated)}
                                        </div>
                                    )}
                                    <div style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        left: '10px',
                                        width: '0',
                                        height: '0',
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderBottom: '6px solid #1e293b'
                                    }}></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modern Filters - Tournament and Month */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start', my: 3, flexWrap: 'wrap' }}>
                    {/* Tournament Filter */}
                    <FormControl className="modern-tournament-filter">
                        <Select
                            value={selectedTournament}
                            onChange={handleTournamentChange}
                            displayEmpty
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search sx={{ color: '#9CA3AF', fontSize: 20 }} />
                                </InputAdornment>
                            }
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className="placeholder-text">{t('leaderboard.filterPlaceholder')}</span>;
                                }
                                const tournament = tournaments.find(tn => tn.id === selected);
                                return tournament?.name ? t(`tournaments.${tournament.name}`, { year: tournament.year }) : t('noTournament');
                            }}
                            sx={{
                                minWidth: 300,
                                height: 50,
                                borderRadius: '12px',
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '&:hover': {
                                    border: '1px solid #4DA8DA',
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                },
                                '&.Mui-focused': {
                                    border: '1px solid #4DA8DA',
                                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                                },
                                '& .MuiSelect-select': {
                                    padding: '12px 16px',
                                    paddingLeft: '48px',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontSize: '14px',
                                    color: '#374151',
                                },
                                '& .MuiInputAdornment-root': {
                                    position: 'absolute',
                                    left: '16px',
                                    zIndex: 1,
                                    pointerEvents: 'none',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                                        border: '1px solid #E5E7EB',
                                        mt: 0.5,
                                        '& .MuiMenuItem-root': {
                                            fontFamily: 'Montserrat, sans-serif',
                                            fontSize: '14px',
                                            padding: '12px 16px',
                                            '&:hover': {
                                                backgroundColor: '#F3F4F6',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#EBF8FF',
                                                '&:hover': {
                                                    backgroundColor: '#DBEAFE',
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {tournaments.map((tournament) => (
                                <MenuItem key={tournament.id} value={tournament.id}>
                                    {tournament?.name ? t(`tournaments.${tournament.name}`, { year: tournament.year }) : t('noTournament')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Month Filter */}
                    <FormControl className="modern-month-filter">
                        <Select
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            displayEmpty
                            startAdornment={
                                <InputAdornment position="start">
                                    <CalendarToday sx={{ color: '#9CA3AF', fontSize: 20 }} />
                                </InputAdornment>
                            }
                            renderValue={(selected) => {
                                if (selected === 'all') {
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{t('leaderboard.allMonths')}</span>
                                        </Box>
                                    );
                                }
                                const [year, month] = selected.split('-');
                                return `${getMonthName(parseInt(month))} ${year}`;
                            }}
                            sx={{
                                minWidth: 250,
                                height: 50,
                                borderRadius: '12px',
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '&:hover': {
                                    border: '1px solid #4DA8DA',
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                },
                                '&.Mui-focused': {
                                    border: '1px solid #4DA8DA',
                                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                                },
                                '& .MuiSelect-select': {
                                    padding: '12px 16px',
                                    paddingLeft: '48px',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontSize: '14px',
                                    color: '#374151',
                                },
                                '& .MuiInputAdornment-root': {
                                    position: 'absolute',
                                    left: '16px',
                                    zIndex: 1,
                                    pointerEvents: 'none',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                                        border: '1px solid #E5E7EB',
                                        mt: 0.5,
                                        maxHeight: '400px',
                                        '& .MuiMenuItem-root': {
                                            fontFamily: 'Montserrat, sans-serif',
                                            fontSize: '14px',
                                            padding: '12px 16px',
                                            '&:hover': {
                                                backgroundColor: '#F3F4F6',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#EBF8FF',
                                                '&:hover': {
                                                    backgroundColor: '#DBEAFE',
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="all">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{t('leaderboard.allMonths')}</span>
                                </Box>
                            </MenuItem>
                            {availableMonths.map((monthData) => {
                                const key = `${monthData.year}-${monthData.month}`;
                                return (
                                    <MenuItem key={key} value={key}>
                                        {getMonthName(monthData.month)} {monthData.year}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Box>
            </div>

            {/* Table Container */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                marginTop: '-20px',
                padding: '0px 10px 10px 10px'
            }}>
                {loading ? (
                    <div className="leagues-loading">
                        <div className="loading-content">
                            <div className="loading-info">
                                <p className="loading-text">{loadingStep}</p>
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${loadingProgress}%` }}
                                    ></div>
                                </div>
                                <p className="progress-percentage">{loadingProgress}%</p>
                            </div>
                        </div>
                    </div>
                ) : error ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                        <Typography variant="h6" color="error">
                            {error}
                        </Typography>
                    </div>
                ) : (
                    <Paper
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            borderRadius: '12px',
                            boxShadow: 'none',
                            overflow: 'hidden',
                            backgroundColor: 'transparent',
                        }}
                    >
                        <TableContainer sx={{ flexGrow: 1, overflowY: 'visible' }}>
                            <Table
                                stickyHeader
                                aria-label="leaderboard table"
                                sx={{
                                    borderCollapse: 'separate',
                                    '& .MuiTableRow-root': {
                                        '&:hover': {
                                            backgroundColor: 'transparent !important',
                                        }
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                backgroundColor: '#F9F9F9',
                                                color: '#787878',
                                                fontWeight: 300,
                                                textTransform: 'uppercase',
                                                fontSize: '14px',
                                                letterSpacing: '0.5px',
                                                borderTopLeftRadius: '7px',
                                                border: 'none',
                                                fontFamily: 'Montserrat, sans-serif',
                                            }}
                                        >
                                            {t('leagues.ranking')}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                backgroundColor: '#F9F9F9',
                                                color: '#787878',
                                                fontWeight: 300,
                                                textTransform: 'uppercase',
                                                fontSize: '14px',
                                                letterSpacing: '0.5px',
                                                border: 'none',
                                                fontFamily: 'Montserrat, sans-serif',
                                            }}
                                        >
                                            {t('leagueDetails.player')}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                backgroundColor: '#F9F9F9',
                                                color: '#787878',
                                                fontWeight: 300,
                                                textTransform: 'uppercase',
                                                fontSize: '14px',
                                                letterSpacing: '0.5px',
                                                borderTopRightRadius: '7px',
                                                border: 'none',
                                                fontFamily: 'Montserrat, sans-serif',
                                            }}
                                        >
                                            {t('leagueDetails.points')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rankedPageItems.length > 0 ? (
                                        rankedPageItems.map((player, index) => {
                                            const { rank } = player;

                                            const rowColor = rank === 1
                                                ? 'rgba(255, 215, 0, 0.075)'
                                                : rank === 2
                                                    ? 'rgba(192, 192, 192, 0.075)'
                                                    : rank === 3
                                                        ? 'rgba(205, 127, 50, 0.075)'
                                                        : '#FFFFFF';

                                            const hoverColor = rank === 1
                                                ? 'rgba(255, 215, 0, 0.2)'
                                                : rank === 2
                                                    ? 'rgba(192, 192, 192, 0.2)'
                                                    : rank === 3
                                                        ? 'rgba(205, 127, 50, 0.2)'
                                                        : '#FAFAFA';

                                            const textColor = rank === 1
                                                ? '#998100'
                                                : rank === 2
                                                    ? '#9A9A9A'
                                                    : rank === 3
                                                        ? '#CD7F32'
                                                        : 'inherit';

                                            const isCurrentUser =
                                                (user && typeof user.id !== 'undefined' && player.userId === user.id) ||
                                                (user && player.username === user.username);

                                            const userRowStyle = isCurrentUser
                                                ? { backgroundColor: '#e0f7fa', fontWeight: 'bold' }
                                                : {};

                                            const prevOnPage = index > 0 ? rankedPageItems[index - 1] : null;
                                            const isSamePointsAsPrevious = prevOnPage && prevOnPage.points === player.points;
                                            const isRanked = rank <= 3;

                                            return (
                                                <TableRow
                                                    key={`${player.userId}-${index}`}
                                                    sx={{
                                                        backgroundColor: rowColor + ' !important',
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        borderBottom: '1px solid #e0e0e0',
                                                        '&:hover': {
                                                            backgroundColor: hoverColor + ' !important',
                                                            '& td': {
                                                                backgroundColor: hoverColor + ' !important',
                                                            }
                                                        },
                                                        cursor: 'default',
                                                    }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: isRanked ? 'bold' : 'normal',
                                                            border: 'none',
                                                            color: textColor,
                                                            width: '80px',
                                                            maxWidth: '80px',
                                                            textAlign: 'center',
                                                            fontFamily: 'Montserrat, sans-serif',
                                                            backgroundColor: rowColor + ' !important',
                                                        }}
                                                    >
                                                        {rank === 1 ? '🏆' :
                                                            rank === 2 ? '🥈' :
                                                                rank === 3 ? '🥉' :
                                                                    isSamePointsAsPrevious ? '-' : rank}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: isRanked ? 'bold' : 'normal',
                                                            border: 'none',
                                                            fontFamily: 'Montserrat, sans-serif',
                                                            color: textColor,
                                                            backgroundColor: rowColor + ' !important',
                                                            ...userRowStyle
                                                        }}
                                                    >
                                                        {player.username}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: isRanked ? 'bold' : 'normal',
                                                            border: 'none',
                                                            color: textColor,
                                                            width: '80px',
                                                            maxWidth: '80px',
                                                            textAlign: 'center',
                                                            fontFamily: 'Montserrat, sans-serif',
                                                            backgroundColor: rowColor + ' !important',
                                                        }}
                                                    >
                                                        {player.points}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                {t('leaderboard.noData', { defaultValue: 'No leaderboard data available for this tournament.' })}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 25, 100]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage={t('rowsPerPage')}
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}—${to} ${t('of')} ${count !== -1 ? count : `${t('common.moreThan')} ${to}`}`
                            }
                        />
                    </Paper>
                )}
            </div>
        </div>
    );
};

export default Leaderboard;